import { ColumnConfig } from './columnConfig';

const LOCAL_STORAGE_COLUMN_ORDER_KEY = 'operationListColumnOrder';

// Event to broadcast column order changes
export const COLUMN_ORDER_CHANGE_EVENT = 'columnOrderChange';

/**
 * Saves the current column order to localStorage and dispatches an event
 */
export const saveColumnOrder = (columnOrder: string[]): void => {
    localStorage.setItem(LOCAL_STORAGE_COLUMN_ORDER_KEY, JSON.stringify(columnOrder));

    // Dispatch event to notify components about column order change
    const event = new CustomEvent(COLUMN_ORDER_CHANGE_EVENT, { detail: columnOrder });
    window.dispatchEvent(event);
};

/**
 * Retrieves the saved column order from localStorage or returns null if not found
 */
export const getSavedColumnOrder = (): string[] | null => {
    const savedOrder = localStorage.getItem(LOCAL_STORAGE_COLUMN_ORDER_KEY);

    if (savedOrder) {
        try {
            return JSON.parse(savedOrder);
        } catch (e) {
            console.error('Failed to parse saved column order', e);
            return null;
        }
    }

    return null;
};

/**
 * Applies saved column order to the provided columns array
 * If no saved order exists or if the saved order is incompatible with current columns,
 * the original columns array is returned
 */
export const applyColumnOrder = (columns: ColumnConfig[]): ColumnConfig[] => {
    const savedOrder = getSavedColumnOrder();

    // If no saved order, return original columns
    if (!savedOrder) {
        return columns;
    }

    // Get a map of all column IDs from the input array
    const columnMap = new Map<string, ColumnConfig>();
    columns.forEach((column) => columnMap.set(column.id, column));

    // Create a new ordered array based on the saved order
    const orderedColumns: ColumnConfig[] = [];

    // First add columns in the saved order if they exist in the current columns
    savedOrder.forEach((id) => {
        const column = columnMap.get(id);
        if (column) {
            orderedColumns.push(column);
            columnMap.delete(id);
        }
    });

    // Add any remaining columns that weren't in the saved order
    columnMap.forEach((column) => orderedColumns.push(column));

    return orderedColumns;
};

/**
 * Reorders columns by moving a column from one index to another
 */
export const reorderColumns = (columns: ColumnConfig[], fromIndex: number, toIndex: number): ColumnConfig[] => {
    if (fromIndex === toIndex) return columns;

    const result = [...columns];
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);

    return result;
};
